// src/pages/invoicing/pi-print.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../config";

const PIPrint = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [brand, setBrand] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    fetchInvoiceData(id);
  }, [id]);

  useEffect(() => {
    if (invoice && billing) {
      document.title = invoice.pi_no; // ✅ Title set as PI number for PDF filename
      setTimeout(() => window.print(), 500);
    }
  }, [invoice, billing]);

  const fetchInvoiceData = async (pi_id) => {
    try {
      const piRes = await fetch(`${API_BASE_URL}/invoicing/get_pi/${pi_id}`, {
        credentials: "include",
      });
      const piData = await piRes.json();
      if (piData.status !== "success") throw new Error("PI not found");
      setInvoice(piData.invoice);

      const itemRes = await fetch(
        `${API_BASE_URL}/invoicing/get_pi_items/${pi_id}`,
        { credentials: "include" },
      );
      const itemData = await itemRes.json();
      if (itemData.status === "success") setLineItems(itemData.items);

      if (piData.invoice.brand_id) {
        const brandRes = await fetch(
          `${API_BASE_URL}/invoicing/get_brand_by_id/${piData.invoice.brand_id}`,
          { credentials: "include" },
        );
        const brandData = await brandRes.json();
        if (brandData.status === "success") setBrand(brandData.brand);
      }

      const billingRes = await fetch(
        `${API_BASE_URL}/invoicing/get_billing_address`,
        { credentials: "include" },
      );
      const billingData = await billingRes.json();
      if (billingData.status === "success")
        setBilling(billingData.billing_address);
    } catch (err) {
      console.error("Failed to fetch invoice:", err);
    }
  };

  return (
    <div
        className="print-root"
        style={{
            width: "210mm",
            padding: "12mm",
            margin: "0 auto",
            boxSizing: "border-box",
            background: "#fff",
        }}
        >
      <style>
        {`
        body {
          font-family: Arial, sans-serif;
          color: #000;
          -webkit-print-color-adjust: exact;
          background-color: #fff;
        }
        h3, h6, strong {
          color: ${brand?.color_code || "#000"};
        }
        img {
          max-width: 250px;
          object-fit: contain;
        }
        
        .d-flex {
          display: flex;
          justify-content: space-between;
        }
        .text-right {
          text-align: right;
        }
        .text-left {
          text-align: left;
        }
        table {
          width: 100%;
          font-size: 14px;
          border-collapse: collapse;
        }
        
        @media print {
  @page { size: A4; margin: 0; }

  html, body {
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .print-root,
  .invoice-box {
    break-inside: avoid-page;
    page-break-inside: avoid;
    page-break-before: avoid;
    page-break-after: avoid;
  }
}

  body {
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
  }
}

      `}
      </style>

      {invoice && billing && (
        <div className="invoice-box">
          <div className="bg-light p-3">
            <div className="">
              <h3
                className="font-weight-bold text-uppercase mb-1"
                style={{ color: "#003366" }}
              >
                PROFORMA INVOICE
              </h3>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div style={{ maxWidth: "70%" }}>
                  <h6
                    className="font-weight-bold text-uppercase mb-1"
                    style={{
                      color: brand?.color_code || "#000",
                      fontWeight: "bold",
                    }}
                  >
                    {billing.company_name}
                  </h6>

                  <p className="mb-1">{billing.address}</p>
                  <p className="mb-1">
                    {billing.state}, {billing.city} - {billing.pincode}
                  </p>
                  <p className="mb-1">GSTIN: {billing.gst}</p>
                  <p className="mb-1">PAN: {billing.pan}</p>
                  <p className="mb-1">CIN: {billing.cin}</p>
                  <p className="mb-1">{billing.email}</p>
                  <p className="mb-0">{billing.mobile}</p>
                </div>

                {brand?.logo && (
                  <img
                    src={
                      brand.logo.startsWith("http")
                        ? brand.logo
                        : `${API_BASE_URL.replace("/index.php", "")}/${brand.logo}`
                    }
                    alt={brand.brand_name}
                    style={{
                      maxHeight: "200px", // ✅ Fits in header nicely
                      maxWidth: "250px", // ✅ Prevents very wide logos from breaking layout
                      width: "auto",
                      height: "auto",
                      objectFit: "contain", // ✅ Keeps proportions without stretching
                      display: "block",
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 p-3">
            <div className="d-flex justify-content-between align-items-start mb-3">
              {/* TO Section */}
              <div style={{ maxWidth: "60%" }}>
                <h6
                  className="font-weight-bold text-uppercase mb-1"
                  style={{
                    color: brand?.color_code,
                    fontWeight: "bold",
                  }}
                >
                  TO,
                </h6>

                <p
                  className="mb-1 text-uppercase"
                  style={{ fontWeight: "bold", color: "#003366" }}
                >
                  {invoice.to_company}
                </p>
                <p className="mb-1" style={{ color: "#003366" }}>
                  {invoice.to_address}
                </p>
                <p className="mb-1" style={{ color: "#003366" }}>
                  {invoice.to_city}, {invoice.to_state} - {invoice.to_pincode}
                </p>
                {invoice.to_gstin && (
                  <p className="mb-0" style={{ color: "#003366" }}>
                    GSTIN: {invoice.to_gstin}
                  </p>
                )}
              </div>

              {/* PI Info */}
              <div className="text-left" style={{ marginRight: "50px" }}>
                <p className="mb-1">
                  <strong style={{ color: brand?.color_code }}>No</strong>
                  :<br /> <span style={{ color: "#003366"}}>{invoice.pi_no}</span>
                </p>
                <p className="mb-0">
                  <strong style={{ color: brand?.color_code }}>PI Date</strong>
                  : <br /><span style={{ color: "#003366"}}>{" "}
                  {new Date(invoice.created_on).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="px-2">
            <table className="w-100" style={{ fontSize: "15px" }}>
              <thead>
                <tr>
                  <td colSpan="5">
                    <hr
                      style={{
                        borderTop: "2px dotted #999",
                        margin: "4px 0 10px 0",
                        borderBottom: "none",
                      }}
                    />
                  </td>
                </tr>
                <tr
                  style={{
                    color: brand?.color_code || "#FF6600",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  <th className="text-left" style={{ width: "7%" }}>
                    No
                  </th>
                  <th className="text-left" style={{ width: "9%" }}>
                    SAC
                  </th>
                  <th className="text-left" style={{ width: "58%" }}>
                    Description
                  </th>
                  <th className="text-left" style={{ width: "12%" }}>
                    Tax (%)
                  </th>
                  <th className="text-right" style={{ width: "13%" }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="5">
                    <hr
                      style={{
                        borderTop: "2px dotted #999",
                        margin: "4px 0 10px 0",
                        borderBottom: "none",
                      }}
                    />
                  </td>
                </tr>
                {lineItems.map((item, index) => (
                  <tr key={index} style={{ color: "#003366" , verticalAlign: "top" }}>
                    <td className="text-left">{index + 1}</td>
                    <td className="text-left">{item.sac_code}</td>
                    <td className="text-left" style={{ paddingRight: "10px" }}>{item.title}</td>
                    <td className="text-left">{parseInt(item.gst_percent)}</td>
                    <td className="text-right">
                      ₹{parseFloat(item.amount).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className="bg-light p-4 mt-4 d-flex justify-content-between align-items-start"
            style={{ fontSize: "15px" }}
          >
            {/* Left: Bank Details */}
            <div style={{ maxWidth: "60%" }}>
              <p className="font-weight-bold mb-2" style={{ color: "#003366" }}>
                <strong>For NEFT / IMPS</strong>
              </p>
              <p className="mb-1 font-weight-bold">{billing?.company_name}</p>
              <p className="mb-1">ICICI Bank</p>
              <p className="mb-1">Vashi Branch</p>
              <p className="mb-1">A/C: 015105013645</p>
              <p className="mb-0">IFSC: ICIC0000151</p>
            </div>

            {/* Right: Amount Summary (non-table version) */}
            <div
              className="bg-light px-4 py-4 text-right"
              style={{
                minWidth: "250px",
                fontSize: "15px",
                color: "#003366",
                textAlign: "right",
              }}
            >
              <div className="font-weight-bold mb-2">
                SUBTOTAL: ₹
                {parseFloat(invoice.subtotal).toLocaleString("en-IN")}
              </div>

              {parseFloat(invoice.igst) > 0 ? (
                <div className="mb-2">
                  IGST on {parseFloat(invoice.subtotal).toLocaleString("en-IN")}{" "}
                  @{" "}
                  {Math.round(
                    (parseFloat(invoice.igst) * 100) /
                      parseFloat(invoice.subtotal),
                  )}
                  % : ₹{parseFloat(invoice.igst).toLocaleString("en-IN")}
                </div>
              ) : (
                <>
                  <div className="mb-2">
                    CGST on{" "}
                    {parseFloat(invoice.subtotal).toLocaleString("en-IN")} @{" "}
                    {Math.round(
                      (parseFloat(invoice.cgst) * 100) /
                        parseFloat(invoice.subtotal),
                    )}
                    % : ₹{parseFloat(invoice.cgst).toLocaleString("en-IN")}
                  </div>

                  <div className="mb-2">
                    SGST on{" "}
                    {parseFloat(invoice.subtotal).toLocaleString("en-IN")} @{" "}
                    {Math.round(
                      (parseFloat(invoice.sgst) * 100) /
                        parseFloat(invoice.subtotal),
                    )}
                    % : ₹{parseFloat(invoice.sgst).toLocaleString("en-IN")}
                  </div>
                </>
              )}

              <div className="my-2">
                <hr
                  style={{
                    borderTop: "2px dotted #999",
                    margin: "10px 0",
                  }}
                />
              </div>

              <div
                className="font-weight-bold text-uppercase"
                style={{ fontSize: "14px" }}
              >
                Total Amount Due
              </div>

              <div
                className="font-weight-bolder"
                style={{
                  fontSize: "22px",
                  color: brand?.color_code || "#FF6600", // ✅ Use brand color
                }}
              >
                ₹
                <strong>
                  {Math.round(parseFloat(invoice.grand_total)).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PIPrint;
